import { MutableRefObject, useCallback } from 'react';
import { useTypedQuery } from 'utils/hooks/useTypedQuery';
import { RsiData, RsiTableOnErrorCallback, RsiTableValidator } from '../../../Types';
import { EngagementsOpenChangeSetStatusDocument } from '../../data/__generated__/EngagementsOpenChangeSetStatus.graphql';
import { useImportersIntl } from '../../../translations/useImportersIntl';

export const ENGAGEMENT_ID_URN = 'urn:utmost:*:engagement:fields:engagementId';

export function useValidateTableAgainstEngagementOpenChangeSet(
  openChangeSetIdsRef: MutableRefObject<Set<string>>
): RsiTableValidator {
  const { get } = useTypedQuery(EngagementsOpenChangeSetStatusDocument);
  const { hasPendingChanges } = useImportersIntl();

  return useCallback(
    async (table: RsiData[], addError: RsiTableOnErrorCallback) => {
      const engagementIds = [
        ...new Set(
          table
            .map((row) => row[ENGAGEMENT_ID_URN])
            .filter((id): id is string => typeof id === 'string' && id.length > 0)
        )
      ];

      if (!engagementIds.length) {
        openChangeSetIdsRef.current = new Set();
        return;
      }

      const response = await get({ variables: { engagementIds } });

      const openChangeSetIds = new Set(
        (response.data?.engagementsOpenChangeSetStatus ?? [])
          .filter((item) => item?.hasOpenChangeSets)
          .map((item) => item?.engagementId)
          .filter((id): id is string => !!id)
      );

      openChangeSetIdsRef.current = openChangeSetIds;

      openChangeSetIds.forEach((engagementId) => {
        const index = table.findIndex((row) => row[ENGAGEMENT_ID_URN] === engagementId);
        if (index !== -1) {
          addError(index, ENGAGEMENT_ID_URN, { level: 'warning', message: hasPendingChanges });
        }
      });
    },
    [get, hasPendingChanges, openChangeSetIdsRef]
  );
}
